/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, Inject, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import template from './order-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {defaultCurrencyDeriver} from '../line-item/currency-default-calculator';
import {lineItemCommons} from '../line-item/line-item-commons';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {advertiserFromNavigation} from '../../components/util/navigation-context';
import {updateBudgets} from './order-budgets';
import {OrderBudgetModalService} from './order-budget-modal.service';
import {TeamApiService} from '../admin/team/team-api.service';
import {UserApiService} from '../admin/user/user-api.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {SearchApiService} from '../common/controller/search-api.service';
import {compareById} from '../common/util/object-id-comparison';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {bufferTemplateSignalValue, createBufferedSignalValue, deferSignalStateWrite, deferSignalViewWrite} from '../common/util/template-state';
import {OrderApiService, OrderModel} from './order-api.service';

@Component({
  standalone: false,
  selector: 'adn-order-edit-page',
  template
})
export class OrderEditPageComponent extends SignalAdminResourceEditPageBase<OrderModel> {

  readonly budgetResult = signal<any>({});
  uiBudgets: any[] = [];
  currentBudgetPeriod: any;
  readonly teams = signal<any[]>([]);
  readonly users = signal<any[] | null>(null);
  readonly advertiserResults = signal<any[]>([]);
  perms: any = {};
  objectiveFields: any = {};
  bidSpecificationFields: any = {};
  objectivesAsString: () => string;
  editingModelObjectivesAsString: () => string;
  /**
   * Same hydrate race as line-item: live translations can flip
   * "impressions" → "rendered impressions" mid-check (NG0100).
   */
  private readonly _objectivesAsString = createBufferedSignalValue('');
  private readonly _moneyCurrency = createBufferedSignalValue<any>(undefined);
  private readonly _chartCurrency = createBufferedSignalValue<any>(undefined);
  private readonly currencyRevision = signal(0);

  get displayObjectivesAsString(): string {
    const next = typeof this.objectivesAsString === 'function' ? this.objectivesAsString() : '';
    return bufferTemplateSignalValue(this._objectivesAsString, next);
  }

  get displayMoneyCurrency(): any {
    this.currencyRevision();
    return bufferTemplateSignalValue(this._moneyCurrency, this.moneyCurrency);
  }

  get displayChartCurrency(): any {
    this.currencyRevision();
    return bufferTemplateSignalValue(this._chartCurrency, this.chartCurrency);
  }

  moneyCurrency: any;
  updateCurrencyHook: any = {};

  isPureMarketplace = false;
  isMarketplace = false;
  isAxAdvertiser = false;
  advertiserInfo = false;
  chartCurrency: any;
  showOptimisation = false;
  showAttentionBiddingGoal = false;
  showTeams = false;
  allowsObjectives = false;
  v1Objectives = false;
  allowsRateLimits = false;
  rateLimitTypes: string[] = [];
  canSeeReports = false;
  primaryNetworkCurrency: any;
  blockSubmit = false;

  private workOutDefaultCurrency: any;
  private hasTimeUpdated = false;
  private orderCommons: any;

  constructor(
    private readonly orderApi: OrderApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly $state: StateService,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    private readonly userApi: UserApiService,
    private readonly searchApi: SearchApiService,
    private readonly orderBudgetModalService: OrderBudgetModalService,
    private readonly uiRouter: UIRouter,
    private readonly navigationHistory: NavigationHistoryService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(orderApi, 'order');

    this.primaryNetworkCurrency = LocalNetworkProfile.get('defaultCurrency');
    this.moneyCurrency = localUserProfile.getPreferredCurrency() || this.primaryNetworkCurrency;
    this.chartCurrency = this.moneyCurrency;
    this.workOutDefaultCurrency = defaultCurrencyDeriver(this, localUserProfile, (apply) => {
      deferSignalViewWrite(this.currencyRevision, apply);
    });
    this.isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.isMarketplace = LocalNetworkProfile.isMarketplacePlatform();
    this.perms = LocalUserPermissions.getAllVisPermissions();
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true || this.perms.AX_ADVERTISER;
    this.allowsObjectives = LocalNetworkProfile.isOrderObjectives();
    this.showOptimisation = LocalNetworkProfile.isOrderOptimisation(localUserProfile.get('username'));
    this.showAttentionBiddingGoal = LocalNetworkProfile.getNetworkTag() === 'b7462';
    this.showTeams = this.localNetworkInfo.getInfo('teamsCount') > 1 || !this.isPureMarketplace;

    this.orderCommons = lineItemCommons(
      this,
      this.translate,
      this.appLocale,
      LocalNetworkProfile,
      LocalUserPermissions,
      this.adnTimeShifter,
      this.localNetworkInfo,
      'editingModel'
    );
    this.orderCommons.setObjectiveFields();
    this.orderCommons.setBidSpecificationFields();
    this.orderCommons.setObjectivesAsString();
    this.orderCommons.setAxAdvertiser();
    this.objectiveFields = this.orderCommons.objectiveFields || {};
    this.bidSpecificationFields = this.orderCommons.bidSpecificationFields || {};
    this.objectivesAsString = this.editingModelObjectivesAsString;
    this.isAxAdvertiser = this.orderCommons.isAxAdvertiser;

    if (this.isAxAdvertiser) {
      this.allowsRateLimits = LocalNetworkProfile.isMarketplaceAdvertiserRateLimits();
      this.rateLimitTypes = ['RENDERED_IMPRESSION', 'VISIBLE_IMPRESSION', 'VIEWABLE_IMPRESSION'];
    } else {
      this.allowsRateLimits = true;
      this.rateLimitTypes = ['IMPRESSION', 'RENDERED_IMPRESSION', 'VISIBLE_IMPRESSION', 'VIEWABLE_IMPRESSION', 'CLICK'];
    }

    this.teamApi.listOptions({pageSize: 1001}).subscribe((page) => {
      // [] → results flips *ngFor on the team <select> mid-CD (NG0100).
      deferSignalStateWrite(() => {
        const allTeams = page.results;
        const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        const teams = isViewAllObjects ? allTeams : _.filter(allTeams, (g: any) => {
          return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
        });
        this.teams.set(teams);
        if (_.get(this.editingModel, ['team', 'id'])) {
          this.resolveTeamReference();
          if (this.editingModel.team) {
            this.workOutDefaultCurrency(null, this.editingModel.team, null);
          }
        }
        if (allTeams.length === 1 && teams.length === 1 && !this.editingModel.team) {
          this.editingModel.team = allTeams[0];
        }
      });
    });

    this.userApi.listOptions({minimal: true}).subscribe((page) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => this.users.set(page.results));
    });
  }

  get showAdvertiserField(): boolean {
    return this.perms.MANAGE_ADVERTISING === true || this.perms.AX_ADVERTISER === true;
  }

  readonly compareById = compareById;

  editsMade(): void {
    this.markExternalFormDirty();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  onTeamChange(): void {
    this.workOutDefaultCurrency(null, this.editingModel.team, null);
    this.onFieldChange();
  }

  onAdvertiserChange(): void {
    this.workOutDefaultCurrency(this.editingModel.advertiser, null, null);
    this.onFieldChange();
  }

  onBudgetObjectiveChange(): void {
    this.chartCurrency = _.get(this.editingModel, ['objectives', 'BUDGET', 'currency']) || this.chartCurrency;
    this.onFieldChange();
  }

  protected override initialiseModel(model: OrderModel): OrderModel {
    const initialised = super.initialiseModel(model);
    const optimiseFor = _.isPlainObject(initialised.optimiseFor)
      ? {...initialised.optimiseFor}
      : {};

    return {
      ...initialised,
      objectives: initialised.objectives || {},
      optimiseFor: {
        ...optimiseFor,
        eventType: optimiseFor.eventType || null
      }
    };
  }

  advertiserSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchApi.search({q: searchElement.search, types: 'Advertiser'}).subscribe((data) => {
      deferSignalStateWrite(() => this.advertiserResults.set(data.searchResults));
    });
  };

  changedObjectiveEndDate = (): void => {
    if (!this.hasTimeUpdated && !_.isDate(this.editingModel.objectiveEndDate)) {
      const networkTimezone = this.LocalNetworkProfile.get('timeZone') || 'utc';
      this.editingModel.modObjectiveEndDate = moment(this.editingModel.modObjectiveEndDate)
        .tz(networkTimezone)
        .endOf('day')
        .toDate();
      this.hasTimeUpdated = true;
    }
    this.onFieldChange();
  };

  editBudgets(): void {
    this.orderBudgetModalService.open({
      modelObject: _.cloneDeep(this.editingModel),
      moneyCurrency: _.cloneDeep(this.moneyCurrency),
      objTranslation: 'Order',
      persistFields: (modelObject, fields) => firstValueFrom(this.orderApi.savePartial(modelObject, fields))
    }).then((data: any) => {
      if (data.obj) {
        this.budgetResult.set({...this.budgetResult(), data: true});
        const savedFields = _.pick(data.obj, ['budget', 'budgetRollover', 'startDate', 'endDate', 'objectives']);
        _.assign(this.editingModel, _.cloneDeep(savedFields));
        this.state.update({...this.state.model(), ..._.cloneDeep(data.obj)});
        updateBudgets(this, data.obj);
      }
    });
  }

  protected override afterEditingModelReset(): void {
    this.editingModel.objectives = this.editingModel.objectives || {};
    this.editingModel.optimiseFor = this.editingModel.optimiseFor || {eventType: null};
    this.orderCommons.setObjectiveFields();
    _.forEach(_.get(this.editingModel, 'objectives') || {}, (val: any, key: string) => {
      if (val > 0 || _.get(val, 'amount') > 0) {
        this.objectiveFields[key] = true;
      }
    });
    this.orderCommons.setObjectivesAsString();
    this.objectivesAsString = this.editingModelObjectivesAsString;
    this.advertiserInfo = !this.isPureMarketplace || !!this.isNew;
    this.blockSubmit = !!this.readOnly;
    this.chartCurrency = _.get(this.editingModel, ['objectives', 'BUDGET', 'currency'])
      || _.get(this.editingModel, ['budget', 0, 'amount', 'currency']);
    // Use API model objectives, not editingModel — setObjectiveFields adds a BUDGET placeholder.
    this.v1Objectives = !_.isEmpty(this.model?.objectives) || this.LocalNetworkProfile.getNetworkId() === '3d65a1';

    if (this.editingModel.objectiveStartDate) {
      this.editingModel.modObjectiveStartDate = this.editingModel.objectiveStartDate;
    }
    if (this.editingModel.objectiveEndDate) {
      this.editingModel.modObjectiveEndDate = this.editingModel.objectiveEndDate;
    }

    const advertiser = advertiserFromNavigation(this.navigationHistory, this.uiRouter.globals.params);
    if (advertiser) {
      this.editingModel.advertiser = advertiser;
    }
    if (_.get(this.editingModel, ['advertiser', 'id'])) {
      this.advertiserResults.set([this.editingModel.advertiser]);
      this.workOutDefaultCurrency(this.editingModel.advertiser, null, null);
    }
    this.resolveTeamReference();
    updateBudgets(this, this.editingModel);
    if (_.get(this.editingModel, ['optimiseFor', 'customEventType', 'id'])) {
      this.editingModel.optimiseFor.eventType = 'CUSTOM';
    }
    if (this.editingModel.advertiser) {
      this.workOutDefaultCurrency(this.editingModel.advertiser, null, null);
      this.chartCurrency = _.get(this.editingModel, ['objectives', 'BUDGET', 'currency']) || this.chartCurrency;
    }
    this.resolveTeamReference();
    this.budgetResult.set({...this.budgetResult(), data: false});
  }

  onOrderCreativesAdvertiserFixed(saved: OrderModel): void {
    if (saved) {
      this.saveDirect(saved);
    }
  }

  protected override prepareSubmitPayload(payload: OrderModel): void {
    if (!_.get(payload.advertiser, 'id')) {
      payload.advertiser = null;
    }
    payload.objectiveStartDate = _.isDate(payload.modObjectiveStartDate)
      ? payload.modObjectiveStartDate
      : null;
    payload.objectiveEndDate = _.isDate(payload.modObjectiveEndDate)
      ? payload.modObjectiveEndDate
      : null;

    if (!_.get(payload, ['salespersonUser', 'name']) && _.get(payload, ['salespersonUser', 'id'])) {
      delete payload.salespersonUser;
    }
    if (!_.get(payload, ['adOpsUser', 'name']) && _.get(payload, ['adOpsUser', 'id'])) {
      delete payload.adOpsUser;
    }

    if (!_.get(payload, ['objectives', 'BUDGET', 'amount'])) {
      _.set(payload, ['objectives', 'BUDGET'], null);
    }

    if (_.get(payload, ['optimiseFor', 'eventType']) === 'CUSTOM') {
      payload.optimiseFor.customEventType = {id: '1q9qmf5z0b2ry1qd'};
    } else if (_.get(payload, ['optimiseFor', 'customEventType'])) {
      payload.optimiseFor.customEventType = null;
    }
  }

  private resolveTeamReference(): void {
    const teams = this.teams();
    if (!this.editingModel?.team || !teams.length) {
      return;
    }
    const teamId = _.isString(this.editingModel.team)
      ? this.editingModel.team
      : this.editingModel.team.id;
    const match = _.find(teams, ['id', teamId]);
    if (match) {
      this.editingModel.team = match;
    }
  }
}
